'use client';

import { useEffect, useRef } from 'react';

const COOKIE_CONSENT_KEY = 'acv_cookie_consent';
const ROCKET_WEB_SRC =
  'https://static.rocket.new/rocket-web.js?_cfg=https%3A%2F%2Femmacontro1939back.builtwithrocket.new&_be=https%3A%2F%2Fappanalytics.rocket.new&_v=0.1.20';
const ROCKET_SHOT_SRC = 'https://static.rocket.new/rocket-shot.js?v=0.0.3';
const RELOAD_FLAG_KEY = 'acv_analytics_reload';

function getAnalyticsConsent(): boolean | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!raw) return null;
    const record = JSON.parse(raw);
    return record?.preferences?.analytics === true;
  } catch {
    return null;
  }
}

function injectScript(src: string, type: 'module' | 'text/javascript', defer: boolean): HTMLScriptElement {
  const el = document.createElement('script');
  el.src = src;
  el.type = type;
  if (defer) el.defer = true;
  else el.async = true;
  document.body.appendChild(el);
  return el;
}

function removeInjectedScripts() {
  [ROCKET_WEB_SRC, ROCKET_SHOT_SRC].forEach((src) => {
    document.querySelectorAll(`script[src="${src}"]`).forEach((el) => el.remove());
  });
}

export default function AnalyticsScripts() {
  const injectedRef = useRef(false);

  useEffect(() => {
    // Handle post-revoke reload: clear the flag and stop — scripts must not load
    if (sessionStorage.getItem(RELOAD_FLAG_KEY) === 'revoked') {
      sessionStorage.removeItem(RELOAD_FLAG_KEY);
      return;
    }

    const consent = getAnalyticsConsent();

    if (consent === true && !injectedRef.current) {
      injectedRef.current = true;
      injectScript(ROCKET_WEB_SRC, 'module', false);
      injectScript(ROCKET_SHOT_SRC, 'module', true);
    }

    // Listen for storage changes (consent updated in another tab or by CookieBanner)
    const handleStorage = (e: StorageEvent) => {
      if (e.key !== COOKIE_CONSENT_KEY) return;

      let newAnalytics: boolean | null = null;
      try {
        const record = e.newValue ? JSON.parse(e.newValue) : null;
        newAnalytics = record?.preferences?.analytics === true;
      } catch {
        newAnalytics = false;
      }

      if (newAnalytics === true && !injectedRef.current) {
        injectedRef.current = true;
        injectScript(ROCKET_WEB_SRC, 'module', false);
        injectScript(ROCKET_SHOT_SRC, 'module', true);
      } else if (newAnalytics === false && injectedRef.current) {
        // Consent revoked: remove scripts and force a controlled reload
        removeInjectedScripts();
        injectedRef.current = false;
        sessionStorage.setItem(RELOAD_FLAG_KEY, 'revoked');
        window.location.reload();
      }
    };

    // Also listen for same-tab consent changes via a custom event dispatched by CookieBanner
    const handleConsentChange = () => {
      const updated = getAnalyticsConsent();

      if (updated === true && !injectedRef.current) {
        injectedRef.current = true;
        injectScript(ROCKET_WEB_SRC, 'module', false);
        injectScript(ROCKET_SHOT_SRC, 'module', true);
      } else if (updated === false && injectedRef.current) {
        removeInjectedScripts();
        injectedRef.current = false;
        sessionStorage.setItem(RELOAD_FLAG_KEY, 'revoked');
        window.location.reload();
      }
    };

    window.addEventListener('storage', handleStorage);
    window.addEventListener('acv_consent_updated', handleConsentChange);

    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('acv_consent_updated', handleConsentChange);
    };
  }, []);

  return null;
}
